import {
  LuArrowRight,
  LuBellRing,
  LuCheckCheck,
  LuCircleAlert,
  LuInfo,
  LuShieldAlert,
} from "react-icons/lu";
import type { IconType } from "react-icons";
import { useQuery } from "@/lib/convex";
import { useMutation } from "convex/react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { flag, searchUpdate } from "@/lib/search";
import { useState } from "react";
import {
  Badge,
  Box,
  Card,
  Flex,
  Button,
  Group,
  Heading,
  HStack,
  Icon,
  Stack,
  Text,
} from "@chakra-ui/react";
import type { Id } from "@convex/_generated/dataModel";
import { api } from "@convex/_generated/api";
import type { NotificationCategoryValue } from "@convex/schemaEnums";
import { ListSkeleton } from "../components/LoadingSkeletons";
import { NotificationMessage } from "../components/NotificationMessage";
import { PageHeader } from "../components/PageHeader";
import { useEffectivePersonId } from "../impersonation";
import { toastError, toastSuccess } from "../components/ui/toaster";
import { readableError } from "@/lib/errors";

const notificationPresentation: Record<
  NotificationCategoryValue,
  {
    label: string;
    icon: IconType;
    colorPalette: string;
  }
> = {
  action_required: {
    label: "Action required",
    icon: LuCircleAlert,
    colorPalette: "orange",
  },
  update: {
    label: "Update",
    icon: LuCheckCheck,
    colorPalette: "green",
  },
  system: {
    label: "System",
    icon: LuInfo,
    colorPalette: "cyan",
  },
  security: {
    label: "Security",
    icon: LuShieldAlert,
    colorPalette: "red",
  },
  invitation: {
    label: "Invitation",
    icon: LuBellRing,
    colorPalette: "teal",
  },
};

export function formatRelativeTime(timestamp: number) {
  const elapsedMs = Date.now() - timestamp;

  if (elapsedMs < 60_000) {
    return "Just now";
  }

  const minutes = Math.floor(elapsedMs / 60_000);
  if (minutes < 60) {
    return `${minutes} minute${minutes === 1 ? "" : "s"} ago`;
  }

  const hours = Math.floor(elapsedMs / 3_600_000);
  if (hours < 24) {
    return `${hours} hour${hours === 1 ? "" : "s"} ago`;
  }

  const days = Math.floor(elapsedMs / 86_400_000);
  if (days === 1) {
    return "Yesterday";
  }
  if (days < 7) {
    return `${days} days ago`;
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(timestamp);
}

export function validateNotificationsSearch(search: Record<string, unknown>): { unread?: true } {
  return { unread: flag(search.unread) };
}

export function NotificationsPage() {
  const navigate = useNavigate();
  const effectivePersonId = useEffectivePersonId();
  const notifications = useQuery(api.notifications.get, {
    effectivePersonId: effectivePersonId as Id<"persons"> | null,
  });
  const markRead = useMutation(api.notifications.markRead);
  const markAllRead = useMutation(api.notifications.markAllRead);

  // ?unread=1 for the unread ones only.
  const unreadOnly = useSearch({ from: "/_authed/notifications" }).unread === true;
  const navigateHere = useNavigate({ from: "/notifications" });
  const setUnreadOnly = (next: boolean) =>
    void navigateHere({ search: { unread: next ? true : undefined }, ...searchUpdate });
  const [busy, setBusy] = useState(false);

  const unread = (notifications ?? []).filter((item) => !item.isRead).length;
  const shown = (notifications ?? []).filter((item) => !unreadOnly || !item.isRead);

  /**
   * Opening a notification is what marks it read.
   *
   * Not a tick-box beside it: the reason to read one is to go and do the thing
   * it is about, and asking somebody to acknowledge it separately is asking
   * them to say twice that they have seen it. A notification with nowhere to
   * go is still marked, because reading it is all there is to do.
   */
  async function open(item: { _id: Id<"notifications">; isRead: boolean; actionUrl?: string }) {
    if (!item.isRead) {
      void markRead({
        notificationId: item._id,
        isRead: true,
        effectivePersonId: effectivePersonId as Id<"persons"> | null,
      }).catch((error) => {
        const { message, detail } = readableError(error, "That could not be marked as read");
        toastError(message, detail);
      });
    }
    if (item.actionUrl) {
      await navigate({ to: item.actionUrl });
    }
  }

  async function onMarkAllRead() {
    setBusy(true);
    try {
      const result = await markAllRead({
        effectivePersonId: effectivePersonId as Id<"persons"> | null,
      });
      toastSuccess(result.marked === 0 ? "Nothing was unread" : `${result.marked} marked as read`);
    } catch (error) {
      const { message, detail } = readableError(error, "They could not be marked as read");
      toastError(message, detail);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Box minH="full" bg="bg.subtle">
      <PageHeader
        title="Notifications"
        description="What has happened to your work, and what is waiting for you."
        breadcrumbs={[{ label: "CeMMbase", to: "/" }, { label: "Notifications" }]}
        actions={
          <HStack gap={3} wrap="wrap">
            <Group attached>
              <Button
                size="sm"
                colorPalette="blue"
                variant={unreadOnly ? "outline" : "solid"}
                onClick={() => setUnreadOnly(false)}
              >
                All
              </Button>
              <Button
                size="sm"
                colorPalette="blue"
                variant={unreadOnly ? "solid" : "outline"}
                onClick={() => setUnreadOnly(true)}
              >
                Unread{unread === 0 ? "" : ` (${unread})`}
              </Button>
            </Group>
            <Button
              size="sm"
              variant="outline"
              loading={busy}
              disabled={unread === 0}
              onClick={() => void onMarkAllRead()}
            >
              <LuCheckCheck />
              Mark all read
            </Button>
          </HStack>
        }
      />

      <Stack gap={3} px={{ base: 6, sm: 8, lg: 10 }} py={{ base: 6, lg: 8 }}>
        {notifications === undefined ? (
          <ListSkeleton label="Loading notifications" rows={6} />
        ) : shown.length === 0 ? (
          <Card.Root borderWidth="1px" borderStyle="dashed" borderColor="border">
            <Card.Body p={6}>
              <Text fontSize="sm" color="fg.muted">
                {unreadOnly && notifications.length > 0
                  ? "Nothing unread."
                  : "Nothing yet. This is where you are told what is waiting for you."}
              </Text>
            </Card.Body>
          </Card.Root>
        ) : (
          shown.map((item) => {
            const presentation = notificationPresentation[item.category];
            const NotifIcon = presentation.icon;
            const hasSomewhereToGo = Boolean(item.actionUrl);

            return (
              <Card.Root
                key={item._id}
                borderWidth="1px"
                borderColor={item.isRead ? "border" : "blue.300"}
                bg={item.isRead ? undefined : "bg"}
                cursor="pointer"
                _hover={{ borderColor: "blue.400" }}
                onClick={() => void open(item)}
              >
                <Card.Body gap={3} p={5}>
                  <Flex align="flex-start" justify="space-between" gap={4} wrap="wrap">
                    <HStack align="flex-start" gap={4} minW="18rem" flex="1">
                      <Icon
                        as={NotifIcon}
                        boxSize={5}
                        mt={1}
                        flexShrink={0}
                        color={item.isRead ? "fg.muted" : `${presentation.colorPalette}.500`}
                      />
                      <Stack gap={1} minW={0}>
                        <HStack flexWrap="wrap" gap={2}>
                          <Heading size="sm" fontWeight={item.isRead ? "medium" : "semibold"}>
                            {item.title}
                          </Heading>
                          {/* A prompt that stopped asking: said, or it would
                              look as if it had been read for them. */}
                          {item.dealtWith ? (
                            <Badge colorPalette="gray" variant="outline">Dealt with</Badge>
                          ) : (
                            <Badge colorPalette={presentation.colorPalette}>
                              {presentation.label}
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="sm" color="fg.muted"><NotificationMessage message={item.message} /></Text>
                      </Stack>
                    </HStack>

                    <HStack gap={3} fontSize="xs" color="fg.muted" flexShrink={0}>
                      <Text>{formatRelativeTime(item.createdAt)}</Text>
                      {hasSomewhereToGo ? <Icon as={LuArrowRight} boxSize={4} /> : null}
                    </HStack>
                  </Flex>
                </Card.Body>
              </Card.Root>
            );
          })
        )}
      </Stack>
    </Box>
  );
}
